import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowNarrowRight,
  Badge,
  Button,
  CellText,
  Copy01,
  DotsVertical,
  Edit05,
  EmptyState,
  Menu,
  Modal,
  Pagination,
  PauseCircle,
  PlayCircle,
  SearchLg,
  Sparkline,
  Table,
  Trash01,
} from "@opendate-app/setlist";

import { useCampaignsContext } from "./CampaignsContainer";
import CancelWorkflowExecutionsModal from "../CustomCampaigns/Sidebars/CancelWorkflowExecutionsModal";
import VenueSelectionModal from "../shared/VenueSelectionModal";
import { rate } from "../Broadcasts/broadcastIndexConfig";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import decodeHtml from "./decodeHtml";

const qs = require("qs");

const PAGE_SIZE = 25;
const DT_COLUMNS = ["name", "delivered", "opened", "clicked", "converted", "bounced", "suppressed", "tools"];
const RATE_METRICS = ["opened", "clicked", "converted", "bounced", "suppressed"];

const STATUSES = {
  active: { label: "Active", color: "success" },
  paused: { label: "Paused", color: "warning" },
};
const CREATED_STATUS = { label: "Created", color: "gray" };

const descriptionStyle = {
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
};

const metricStyle = {
  width: 64,
  marginLeft: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  whiteSpace: "nowrap",
};

const buildParams = (draw, page, sort) => {
  const columns = {};
  DT_COLUMNS.forEach((data, index) => {
    columns[index] = { data, searchable: false, orderable: data !== "tools", search: { value: "", regex: false } };
  });

  return {
    draw,
    start: (page - 1) * PAGE_SIZE,
    length: PAGE_SIZE,
    search: { value: "", regex: false },
    columns,
    order: { 0: { column: DT_COLUMNS.indexOf(sort.key), dir: sort.direction } },
  };
};

const parsePoints = (json) => JSON.parse(json || "[]");

const CampaignIndexDatatable = () => {
  const {
    team,
    copyCampaign,
    updateCampaign,
    updateCustomCampaign,
    deleteCustomCampaign,
    moveCustomCampaign,
    venueOwnerships,
    dataTableSource,
    refreshDataTable,
    dataTableRefreshedAt,
    setCampaignCount,
  } = useCampaignsContext();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: "name", direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [pauseCampaignData, setPauseCampaignData] = useState(null);
  const [moveCampaignData, setMoveCampaignData] = useState(null);
  const [deleteCampaignData, setDeleteCampaignData] = useState(null);
  const drawRef = useRef(0);

  useEffect(() => {
    let ignore = false;
    drawRef.current += 1;
    setLoading(true);
    setLoadFailed(false);

    axios.get(dataTableSource, {
      params: buildParams(drawRef.current, page, sort),
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "brackets" }),
    }).then(({ data }) => {
      if (ignore) return;
      const records = data.data || [];
      const lastPage = Math.max(1, Math.ceil((data.recordsTotal || 0) / PAGE_SIZE));
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setRows(records);
      setTotal(data.recordsTotal || 0);
      setCampaignCount(data.recordsTotal || 0);
    }).catch(() => {
      if (ignore) return;
      setLoadFailed(true);
      toastError("Could not load automations, please try again");
    }).finally(() => {
      if (!ignore) setLoading(false);
    });

    return () => { ignore = true; };
  }, [dataTableSource, dataTableRefreshedAt, page, sort]);

  const updateFunctionFor = (campaignType) => (campaignType === "Campaign" ? updateCampaign : updateCustomCampaign);

  const checkInProgressWorkflowExecutions = async (customCampaignId) => {
    try {
      const params = new URLSearchParams();
      params.append("q[state_in][]", "created");
      params.append("q[state_in][]", "running");

      const response = await axios.get(`/teams/${team.id}/custom_campaigns/${customCampaignId}/workflow_executions?${params.toString()}`);
      return response.data.total_count || 0;
    } catch (_error) {
      toastError("Error checking workflow executions");
      return 0;
    }
  };

  const handlePauseCampaign = async (campaignId, campaignType) => {
    if (campaignType === "CustomCampaign") {
      const count = await checkInProgressWorkflowExecutions(campaignId);
      if (count > 0) {
        setInProgressCount(count);
        setPauseCampaignData({ campaignId, campaignType });
        setShowCancelModal(true);
        return;
      }
    }

    updateFunctionFor(campaignType)(campaignId, { state: "paused" }, refreshDataTable);
  };

  const handleContinueWorkflows = () => {
    setShowCancelModal(false);
    if (pauseCampaignData) {
      const { campaignId, campaignType } = pauseCampaignData;
      updateFunctionFor(campaignType)(campaignId, { state: "paused" }, refreshDataTable);
      setPauseCampaignData(null);
    }
  };

  const handleCancelWorkflows = async () => {
    setShowCancelModal(false);

    if (pauseCampaignData) {
      const { campaignId, campaignType } = pauseCampaignData;

      try {
        axios.defaults.headers.common["X-CSRF-TOKEN"] = document.querySelector('meta[name="csrf-token"]').content;
        await axios.post(`/teams/${team.id}/custom_campaigns/${campaignId}/cancel_in_progress_workflow_executions`);
        toastSuccess("Workflows cancelled");
        updateFunctionFor(campaignType)(campaignId, { state: "paused" }, refreshDataTable);
      } catch (_error) {
        toastError("Error cancelling workflows");
      }

      setPauseCampaignData(null);
    }
  };

  const handleCloseModal = () => {
    setShowCancelModal(false);
    setPauseCampaignData(null);
  };

  const menuItems = (row) => {
    const editItem = { label: "Edit Automation", icon: Edit05, onClick: () => { window.location.href = row.campaign_url; } };
    const stateItem = {
      active: { label: "Pause Automation", icon: PauseCircle, onClick: () => handlePauseCampaign(row.id, row.campaign_type) },
      paused: { label: "Resume Automation", icon: PlayCircle, onClick: () => updateFunctionFor(row.campaign_type)(row.id, { state: "active" }, refreshDataTable) },
    }[row.state];
    const baseItems = stateItem ? [editItem, stateItem] : [editItem];

    if (row.campaign_type !== "CustomCampaign") return baseItems;

    return [
      ...baseItems,
      { label: "Duplicate Automation", icon: Copy01, onClick: () => copyCampaign(row) },
      ...(venueOwnerships && venueOwnerships.length > 1
        ? [{ label: "Change Venue", icon: ArrowNarrowRight, onClick: () => setMoveCampaignData(row) }]
        : []),
      { label: "Delete Automation", icon: Trash01, destructive: true, onClick: () => setDeleteCampaignData(row) },
    ];
  };

  const metricCell = (key) => (row) => {
    const value = RATE_METRICS.includes(key)
      ? rate(row[`${key}_count`], row.delivered_count)
      : Number(row.delivered_count).toLocaleString();

    return (
      <div style={metricStyle}>
        <Sparkline points={parsePoints(row[`${key}_data`])} height={20} />
        <span style={{ textAlign: "right" }}>{value}</span>
      </div>
    );
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      primary: true,
      sortable: true,
      render: (row) => {
        const status = STATUSES[row.state] || CREATED_STATUS;
        return (
          <div style={{ minWidth: 240 }}>
            <CellText
              primary={(
                <span className="d-inline-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                  <a href={row.campaign_url} className="text-reset text-decoration-none">{decodeHtml(row.campaign_name)}</a>
                  <Badge color={status.color} size="sm" dot>{status.label}</Badge>
                </span>
              )}
              supporting={row.campaign_description ? (
                <span style={descriptionStyle} dangerouslySetInnerHTML={{ __html: row.campaign_description }} />
              ) : null}
            />
          </div>
        );
      },
    },
    ...["delivered", ...RATE_METRICS].map((key) => ({
      key,
      header: key.charAt(0).toUpperCase() + key.slice(1),
      align: "right",
      sortable: true,
      render: metricCell(key),
    })),
    {
      key: "tools",
      header: "",
      width: "48px",
      render: (row) => (
        <Menu
          placement="bottom-end"
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${decodeHtml(row.campaign_name)}`} />}
          items={menuItems(row)}
        />
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <CancelWorkflowExecutionsModal
        open={showCancelModal}
        onClose={handleCloseModal}
        onContinue={handleContinueWorkflows}
        onCancel={handleCancelWorkflows}
        inProgressCount={inProgressCount}
        changeDescription="pausing this automation"
      />
      <VenueSelectionModal
        isOpen={!!moveCampaignData}
        onClose={() => setMoveCampaignData(null)}
        onConfirm={(venueOwnershipId) => {
          moveCustomCampaign(moveCampaignData.id, venueOwnershipId, refreshDataTable);
          setMoveCampaignData(null);
        }}
        venueOwnerships={venueOwnerships}
        currentVenueOwnershipId={moveCampaignData?.venue_ownership_id}
        title="Change Venue"
        subtitle="Select the venue for this automation"
      />
      <Modal
        open={!!deleteCampaignData}
        onClose={() => setDeleteCampaignData(null)}
        type="error"
        title="Delete automation?"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setDeleteCampaignData(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteCustomCampaign(deleteCampaignData.id, refreshDataTable);
                setDeleteCampaignData(null);
              }}
            >
              Delete Automation
            </Button>
          </>
        )}
      >
        <div style={{ fontSize: 14, lineHeight: "20px", color: "var(--sl-text-tertiary)" }}>
          <strong>{decodeHtml(deleteCampaignData?.campaign_name || "")}</strong> will be deleted. This can't be undone.
        </div>
      </Modal>
      <div style={{ padding: "4px 8px" }}>
        <Table
          columns={columns}
          data={rows}
          getRowKey={(row) => `${row.campaign_type}-${row.id}`}
          loading={loading}
          loadingLabel="Loading automations"
          sort={sort}
          onSortChange={(key, direction) => {
            setSort({ key, direction });
            setPage(1);
          }}
          stackBelow={640}
          stickyLastColumn
          error={loadFailed ? "Could not load automations, please try again." : null}
          empty={(
            <EmptyState
              size="sm"
              icon={SearchLg}
              title="No automations found"
              description="Try a different status or search, or create a new automation."
            />
          )}
        />
      </div>
      {totalPages > 1 && (
        <div className="px-4 py-3 border-top">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </>
  );
};

export default CampaignIndexDatatable;
